import React, { useState, useMemo } from 'react';
import { doc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../firebase';
import { configDoc, dailyAnswerDoc, petDoc } from '../db';
import { UserProfile } from '../App';
import PetCharacter, { DEFAULT_CONFIG } from '../components/PetCharacter';
import { inferGender, ProfileGender } from '../utils/gender';

interface Props {
  user: UserProfile;
  onComplete: () => void;
}

const AVATARS = ['👩🏻', '👩🏼', '👩🏽', '👩🏾', '👩🏿', '👨🏻', '👨🏼', '👨🏽', '👨🏾', '👨🏿', '🧑🏻', '🧑🏼', '🧑🏽', '🧑🏾', '🧑🏿', '🦸‍♀️', '🦸‍♂️', '🧚‍♀️', '🧚‍♂️', '🌸'];

const QUESTIONS = [
  "Eğer bir gün sonsuz sürseydi ve onu seninle geçirebilseydim, nerede ve nasıl geçirirdik?",
  "Şu an ne hissediyorsun? Tek kelimeyle değil, gerçekten.",
  "Sevildiğini en çok hangi anda hissediyorsun?",
  "Birlikte geçirdiğimiz anlardan hangisini hiç unutmak istemezsin?",
  "Gelecekte hayal ettiğin en sıradan ortak günümüz nasıl görünüyor?",
];

function getTodayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getFirstQuestion(): string {
  const d = new Date();
  return QUESTIONS[d.getDay() % QUESTIONS.length];
}

function FullConfetti() {
  const COLORS = ['#FF6B9D', '#C77DFF', '#FFD700', '#7dff8f', '#00E5FF', '#FF9800'];
  const p = useMemo(() => Array.from({ length: 55 }, (_, i) => ({
    id: i, x: Math.random() * 100, size: 5 + Math.random() * 9,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    delay: Math.random() * 1, duration: 1.8 + Math.random() * 1.2,
    rotate: Math.random() * 360,
  })), []); // eslint-disable-line
  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 500, overflow: 'hidden' }}>
      {p.map(q => (
        <motion.div key={q.id}
          initial={{ x: `${q.x}vw`, y: -20, opacity: 1, rotate: q.rotate }}
          animate={{ y: '110vh', opacity: 0, rotate: q.rotate + 540 }}
          transition={{ duration: q.duration, delay: q.delay, ease: [0.25, 0.46, 0.45, 0.94] }}
          style={{ position: 'absolute', width: q.size, height: q.size * 0.55, background: q.color, borderRadius: 2 }}
        />
      ))}
    </div>
  );
}

const TOTAL_STEPS = 5;

export default function Setup({ user, onComplete }: Props) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState(user.displayName);
  const [avatar, setAvatar] = useState('');
  const [gender, setGender] = useState<ProfileGender | ''>(user.gender || inferGender(user) || '');
  const [startDate, setStartDate] = useState('');
  const [petName, setPetName] = useState(DEFAULT_CONFIG.name);
  const [questionAnswer, setQuestionAnswer] = useState('');
  const [saving, setSaving] = useState(false);
  const [skippedQuestion, setSkippedQuestion] = useState(false);
  const todayQuestion = getFirstQuestion();

  const next = () => setStep(s => s + 1);

  const saveStep1 = async () => {
    if (!name.trim() || !gender) return;
    setSaving(true);
    await updateDoc(doc(db, 'users', user.uid), {
      displayName: name.trim(),
      gender,
      ...(avatar ? { avatar } : {}),
    });
    setSaving(false);
    next();
  };

  const saveStep2 = async () => {
    if (!startDate) return;
    setSaving(true);
    await setDoc(configDoc(user.coupleId), { startDate }, { merge: true });
    setSaving(false);
    next();
  };

  const savePetStep = async () => {
    const nextName = petName.trim() || DEFAULT_CONFIG.name;
    setSaving(true);
    await setDoc(petDoc(user.coupleId), {
      happiness: 72,
      cleanliness: 72,
      hunger: 72,
      bond: 18,
      totalDays: 0,
      level: 0,
      xp: 0,
      lastFed: serverTimestamp(),
      lastWashed: serverTimestamp(),
      lastPlayed: serverTimestamp(),
      lastActive: serverTimestamp(),
      lastCareAt: serverTimestamp(),
      lastCareDecayAt: serverTimestamp(),
      lastBondAt: serverTimestamp(),
      lastBondDecayDate: getTodayKey(),
      bondDate: getTodayKey(),
      dailyBondGain: 0,
      xpDate: getTodayKey(),
      dailyCareXp: 0,
      dailyCareCount: 0,
      levelUpDate: serverTimestamp(),
      showLevelUp: false,
      characterConfig: { ...DEFAULT_CONFIG, name: nextName },
      createdAt: serverTimestamp(),
    }, { merge: true });
    setSaving(false);
    next();
  };

  const saveQuestionStep = async (skip = false) => {
    if (!skip && !questionAnswer.trim()) return;
    setSaving(true);
    if (!skip && questionAnswer.trim()) {
      await setDoc(dailyAnswerDoc(user.coupleId, getTodayKey()), {
        [user.uid]: { answer: questionAnswer.trim(), name: name.trim() }
      }, { merge: true });
    }
    if (skip) setSkippedQuestion(true);
    setSaving(false);
    next();
  };

  const finish = async () => {
    setSaving(true);
    await updateDoc(doc(db, 'users', user.uid), {
      setupComplete: true,
      displayName: name.trim(),
      gender,
      ...(avatar ? { avatar } : {}),
    });
    setSaving(false);
    onComplete();
  };

  const stepVariants = {
    enter: { opacity: 0, x: 32 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -32 },
  };

  return (
    <div style={styles.container}>
      {/* Progress bar */}
      <div style={styles.progressBar}>
        <motion.div
          animate={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          style={styles.progressFill}
        />
      </div>

      {/* Step dots */}
      <div style={styles.stepDots}>
        {Array.from({ length: TOTAL_STEPS }, (_, i) => (
          <motion.div
            key={i}
            animate={{
              background: i < step ? '#E91E8C' : i === step - 1 ? '#E91E8C' : 'rgba(255,255,255,0.15)',
              scale: i === step - 1 ? 1.3 : 1,
            }}
            style={styles.dot}
          />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* ── STEP 1: İsim + Avatar ── */}
        {step === 1 && (
          <motion.div key="step1" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.28 }} style={styles.stepContent}>
            <span style={{ fontSize: 52 }}>👋</span>
            <h2 style={styles.stepTitle}>Seni nasıl görelim?</h2>
            <p style={styles.stepSub}>İsmin, profilin ve avatarın</p>

            <div style={styles.nameRow}>
              <div style={styles.selectedAvatar}>{avatar || '🌸'}</div>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="İsmin"
                style={styles.nameInput}
                maxLength={20}
              />
            </div>

            <div style={styles.genderRow}>
              <button
                type="button"
                onClick={() => setGender('female')}
                style={{ ...styles.genderBtn, ...(gender === 'female' ? styles.genderBtnActive : {}) }}
              >
                Kadın
              </button>
              <button
                type="button"
                onClick={() => setGender('male')}
                style={{ ...styles.genderBtn, ...(gender === 'male' ? styles.genderBtnActive : {}) }}
              >
                Erkek
              </button>
            </div>

            <div style={styles.avatarGrid}>
              {AVATARS.map(em => (
                <motion.button
                  key={em}
                  whileTap={{ scale: 0.88 }}
                  onClick={() => setAvatar(em)}
                  style={{
                    ...styles.avatarBtn,
                    background: avatar === em ? 'rgba(233,30,140,0.2)' : 'rgba(255,255,255,0.06)',
                    borderColor: avatar === em ? 'rgba(233,30,140,0.6)' : 'rgba(255,255,255,0.1)',
                    boxShadow: avatar === em ? '0 0 12px rgba(233,30,140,0.3)' : 'none',
                  }}
                >
                  {em}
                </motion.button>
              ))}
            </div>

            <motion.button
              whileTap={{ scale: 0.96 }}
              style={styles.primaryBtn}
              onClick={saveStep1}
              disabled={saving || !name.trim() || !gender}
            >
              {saving ? 'Kaydediliyor...' : 'Devam →'}
            </motion.button>
          </motion.div>
        )}

        {/* ── STEP 2: Birliktelik tarihi ── */}
        {step === 2 && (
          <motion.div key="step2" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.28 }} style={styles.stepContent}>
            <span style={{ fontSize: 52 }}>📅</span>
            <h2 style={styles.stepTitle}>Birlikteliğiniz ne zaman başladı?</h2>
            <p style={styles.stepSub}>Bu tarihi birliktelik sayacınızda kullanacağız</p>

            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              style={styles.dateInput}
              max={new Date().toISOString().split('T')[0]}
            />

            {startDate && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} style={styles.datePreview}>
                <span style={{ fontSize: 24 }}>💕</span>
                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }}>
                  {new Date(startDate + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
                  {Math.floor((Date.now() - new Date(startDate).getTime()) / 86400000)} gündür birliktesiniz 🌸
                </p>
              </motion.div>
            )}

            <div style={{ display: 'flex', gap: 10, width: '100%' }}>
              <motion.button whileTap={{ scale: 0.96 }} style={styles.ghostBtn} onClick={() => { setStartDate(''); next(); }}>
                Atla
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.96 }}
                style={{ ...styles.primaryBtn, flex: 1 }}
                onClick={saveStep2}
                disabled={saving || !startDate}
              >
                {saving ? 'Kaydediliyor...' : 'Devam →'}
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* ── STEP 3: Minik'in ilk adı ── */}
        {step === 3 && (
          <motion.div key="step3-pet" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.28 }} style={styles.stepContent}>
            <span style={{ fontSize: 52 }}>✦</span>
            <h2 style={styles.stepTitle}>Minik'in ilk adı</h2>
            <p style={styles.stepSub}>Burası ikinizin küçük evreni. Minik de bunun hafızası olacak.</p>

            <div style={styles.petPreviewCard}>
              <PetCharacter config={{ ...DEFAULT_CONFIG, name: petName.trim() || DEFAULT_CONFIG.name }} size={118} mood="happy" />
              <strong>{petName.trim() || DEFAULT_CONFIG.name}</strong>
              <span>Bugünkü ritimleri, notları ve küçük izleri hatırlayacak.</span>
            </div>

            <input
              value={petName}
              onChange={e => setPetName(e.target.value)}
              placeholder="Minik'in adı"
              style={styles.nameInput}
              maxLength={18}
            />

            <motion.button
              whileTap={{ scale: 0.96 }}
              style={styles.primaryBtn}
              onClick={savePetStep}
              disabled={saving}
            >
              {saving ? 'Kaydediliyor...' : 'Minik hazır →'}
            </motion.button>
          </motion.div>
        )}

        {/* ── STEP 4: İlk soru ── */}
        {step === 4 && (
          <motion.div key="step3" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.28 }} style={styles.stepContent}>
            <span style={{ fontSize: 52 }}>💬</span>
            <h2 style={styles.stepTitle}>İlk göreviniz</h2>
            <p style={styles.stepSub}>İlk küçük bağ izi burada başlasın.</p>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              style={styles.questionCard}
            >
              <p style={styles.questionLabel}>Günün sorusu</p>
              <p style={styles.questionText}>"{todayQuestion}"</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              style={{ width: '100%' }}
            >
              <textarea
                placeholder="Cevabını yaz... (opsiyonel)"
                value={questionAnswer}
                onChange={e => setQuestionAnswer(e.target.value)}
                rows={3}
                style={{ marginBottom: 12 }}
              />
            </motion.div>

            <div style={{ display: 'flex', gap: 10, width: '100%' }}>
              <motion.button
                whileTap={{ scale: 0.96 }}
                style={styles.ghostBtn}
                onClick={() => saveQuestionStep(true)}
                disabled={saving}
              >
                Atla
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.96 }}
                style={{ ...styles.primaryBtn, flex: 1 }}
                onClick={() => saveQuestionStep(false)}
                disabled={saving || !questionAnswer.trim()}
              >
                {saving ? 'Kaydediliyor...' : 'Kaydet →'}
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* ── STEP 5: Kutlama ── */}
        {step === 5 && (
          <motion.div key="step4" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.28 }} style={styles.stepContent}>
            <FullConfetti />

            <motion.span
              animate={{ scale: [1, 1.3, 1], rotate: [0, 15, -15, 0] }}
              transition={{ duration: 0.8, repeat: 3 }}
              style={{ fontSize: 72, display: 'block' }}
            >
              🎉
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              style={styles.stepTitle}
            >
              Uygulamanız hazır!
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              style={styles.stepSub}
            >
              Artık her şey hazır. Birlikte güzel anlar biriktireceksiniz 💕
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              style={styles.summaryCard}
            >
              {avatar && <span style={{ fontSize: 36 }}>{avatar}</span>}
              <div>
                <p style={{ fontWeight: 700, fontSize: 16 }}>{name}</p>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                  {petName.trim() || DEFAULT_CONFIG.name} hazır ✅
                </p>
                {!skippedQuestion && questionAnswer && (
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                    İlk soruyu cevapladın ✅
                  </p>
                )}
                {startDate && (
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                    Birliktelik tarihi kaydedildi ✅
                  </p>
                )}
              </div>
            </motion.div>

            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1, type: 'spring', stiffness: 200, damping: 16 }}
              whileTap={{ scale: 0.96 }}
              style={styles.primaryBtn}
              onClick={finish}
              disabled={saving}
            >
              {saving ? 'Hazırlanıyor...' : 'Ana Sayfaya Git 💕'}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '0 0 32px', position: 'relative', overflow: 'hidden',
  },
  progressBar: {
    width: '100%', height: 3,
    background: 'rgba(255,255,255,0.08)',
    flexShrink: 0,
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #E91E8C, #C77DFF)',
    borderRadius: '0 2px 2px 0',
  },
  stepDots: {
    display: 'flex', gap: 8, padding: '20px 0 4px',
  },
  dot: { width: 8, height: 8, borderRadius: '50%' },
  stepContent: {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 16, padding: '24px 24px 0', width: '100%', maxWidth: 400, textAlign: 'center',
  },
  stepTitle: {
    fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700,
    background: 'linear-gradient(135deg, #FF6B9D, #C77DFF)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
    margin: 0,
  },
  stepSub: { color: 'rgba(255,255,255,0.5)', fontSize: 14, lineHeight: 1.5, margin: 0 },
  nameRow: { display: 'flex', alignItems: 'center', gap: 12, width: '100%' },
  selectedAvatar: {
    width: 52, height: 52, borderRadius: '50%', fontSize: 28, flexShrink: 0,
    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  nameInput: {
    flex: 1, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 14, padding: '12px 16px', color: 'white', fontSize: 16,
    outline: 'none',
  },
  avatarGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, width: '100%',
  },
  avatarBtn: {
    fontSize: 24, padding: '10px 0',
    borderRadius: 12, border: '1px solid',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  genderRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    width: '100%',
  },
  genderBtn: {
    minHeight: 46,
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.06)',
    color: 'rgba(255,255,255,0.68)',
    fontSize: 14,
    fontWeight: 800,
    cursor: 'pointer',
  },
  genderBtnActive: {
    background: 'rgba(233,30,140,0.18)',
    border: '1px solid rgba(233,30,140,0.5)',
    color: 'white',
  },
  dateInput: {
    width: '100%', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 16, padding: '14px 18px', color: 'white', fontSize: 16,
    outline: 'none', boxSizing: 'border-box',
  },
  datePreview: {
    background: 'rgba(233,30,140,0.08)', border: '1px solid rgba(233,30,140,0.2)',
    borderRadius: 16, padding: '14px 18px', width: '100%',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
  },
  questionCard: {
    background: 'linear-gradient(135deg, rgba(233,30,140,0.12), rgba(156,39,176,0.12))',
    border: '1px solid rgba(233,30,140,0.25)',
    borderRadius: 20, padding: '16px 18px', width: '100%', textAlign: 'left',
  },
  questionLabel: { fontSize: 11, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 },
  questionText: { fontFamily: "'Playfair Display', serif", fontSize: 16, fontStyle: 'italic', lineHeight: 1.55, color: 'rgba(255,255,255,0.9)', margin: 0 },
  petPreviewCard: {
    width: '100%',
    borderRadius: 24,
    padding: '18px 16px',
    background: 'linear-gradient(145deg, rgba(103,232,249,0.12), rgba(191,90,242,0.1))',
    border: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    boxShadow: '0 20px 70px rgba(0,0,0,0.24)',
  },
  petPreviewCardStrong: {},
  summaryCard: {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 20, padding: '16px 20px', width: '100%',
    display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
  },
  primaryBtn: {
    width: '100%', padding: '15px 24px',
    background: 'linear-gradient(135deg, #E91E8C, #9C27B0)',
    border: 'none', borderRadius: 20,
    color: 'white', fontSize: 16, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 6px 24px rgba(233,30,140,0.35)',
  },
  ghostBtn: {
    padding: '15px 20px', minWidth: 80,
    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 20, color: 'rgba(255,255,255,0.6)', fontSize: 14, cursor: 'pointer',
  },
};
